import { AlertTriangle, CheckCircle2, Info, X } from '@/components/ui/icons';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTopLayer } from '@/hooks/useTopLayer';
import { cn } from '@/lib/utils';
import { useConfirm, useToasts } from '@/stores/ui';
import { Button } from './Button';
import { Input } from './Input';
import { Illustration, type IllustrationName } from './Illustration';
import { Modal } from './Modal';

export function EmptyState({
  icon,
  art,
  title,
  body,
  action,
  className,
}: {
  icon?: ReactNode;
  /** A larger illustration, used instead of the icon. */
  art?: IllustrationName;
  title: string;
  body?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-col items-center justify-center gap-3 px-6 py-10 text-center', className)}>
      {art ? <Illustration name={art} className="mb-1 max-w-full" /> : icon && <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-hover text-fg-muted">{icon}</div>}
      <h3 className="text-lg font-bold text-fg">{title}</h3>
      {body && <p className="max-w-sm text-sm text-fg-muted">{body}</p>}
      {action}
    </div>
  );
}

export function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div role="alert" className="flex flex-col items-center gap-3 px-6 py-10 text-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-danger-soft text-danger">
        <AlertTriangle size={22} />
      </div>
      <p className="text-sm text-fg-soft">{message}</p>
      {onRetry && (
        <Button variant="secondary" size="sm" onClick={onRetry}>
          Try again
        </Button>
      )}
    </div>
  );
}

const toastIcon = {
  success: <CheckCircle2 size={18} className="text-success" />,
  error: <AlertTriangle size={18} className="text-danger" />,
  info: <Info size={18} className="text-link" />,
};

export function Toaster() {
  const toasts = useToasts((s) => s.toasts);
  const dismiss = useToasts((s) => s.dismiss);
  const ref = useRef<HTMLDivElement>(null);
  // Keep toasts above any open modal dialog.
  useTopLayer(ref, toasts.length > 0, [toasts]);
  return (
    <div
      ref={ref}
      popover="manual"
      aria-live="polite"
      className="pointer-events-none fixed inset-x-0 top-auto bottom-4 z-[80] m-0 flex w-auto flex-col items-center gap-2 overflow-visible border-0 bg-transparent px-4 sm:right-4 sm:left-auto sm:items-end"
    >
      {toasts.map((t) => (
        <div
          key={t.id}
          role={t.kind === 'error' ? 'alert' : 'status'}
          className="pointer-events-auto flex w-full max-w-sm animate-slide-up items-start gap-3 rounded-xl border border-line bg-elevated px-4 py-3 text-sm text-fg shadow-pop"
        >
          <span className="mt-0.5">{toastIcon[t.kind]}</span>
          <p className="flex-1 leading-snug">{t.message}</p>
          <button type="button" onClick={() => dismiss(t.id)} aria-label="Dismiss" className="rounded p-0.5 text-fg-muted hover:text-fg">
            <X size={16} />
          </button>
        </div>
      ))}
    </div>
  );
}

/** Renders whatever `confirm()` request is pending. Mounted once at the app root. */
export function ConfirmHost() {
  const request = useConfirm((s) => s.request);
  const close = useConfirm((s) => s.close);
  const [value, setValue] = useState('');
  useEffect(() => setValue(request?.input?.value ?? ''), [request]);

  return (
    <Modal
      open={!!request}
      onClose={() => close(false)}
      title={request?.title}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={() => close(false)}>
            Cancel
          </Button>
          <Button variant={request?.danger ? 'danger' : 'primary'} onClick={() => close(true, value)} data-autofocus={!request?.input || undefined}>
            {request?.confirmLabel ?? 'Confirm'}
          </Button>
        </>
      }
    >
      <p className="text-[15px] leading-relaxed text-fg-soft">{request?.body}</p>
      {request?.input && (
        <div className="mt-4">
          {/* Typing goes straight into the box, and Enter confirms. */}
          <Input
            label={request.input.label}
            placeholder={request.input.placeholder}
            type={request.input.type ?? 'text'}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
                e.preventDefault();
                close(true, value);
              }
            }}
            maxLength={500}
            data-autofocus
          />
        </div>
      )}
    </Modal>
  );
}
